import { nriHeroData, nriWhatsappUrl, nriHeroBg } from "../../data/nriTaxData";
import { ArrowRight, Clock, FileText, Globe, TrendingUp, Award } from "lucide-react";

const socialIcons = [FileText, Globe, TrendingUp, Award];

const WaIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 flex-shrink-0">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const NriHeroSection = () => {
  return (
    <section
      id="nri-hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: "#1a1c1b" }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img src={nriHeroBg} alt="" className="w-full h-full object-cover" loading="eager" />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(26,28,27,0.97) 0%, rgba(26,28,27,0.93) 50%, rgba(26,28,27,0.80) 100%)" }}
        />
      </div>

      <div className="relative z-10 max-w-[87.5rem] mx-auto px-10 w-full py-40">
        <div className="max-w-3xl">

          {/* Eyebrow */}
          <div className="mb-10">
            <span
              className="inline-block text-[#888680] text-xs font-medium uppercase tracking-[0.2em] border border-[#3f4816]/40 px-4 py-2 rounded-full"
              style={{ fontFamily: "'Inter', Arial, sans-serif" }}
            >
              {nriHeroData.eyebrow}
            </span>
          </div>

          {/* Headline — clearly separated visual blocks */}
          <div className="mb-6">
            <p
              className="font-semibold uppercase mb-2"
              style={{
                fontFamily: "'Inter', Arial, sans-serif",
                fontSize: "clamp(1rem, 0.8rem + 1vw, 1.4rem)",
                letterSpacing: "-0.01em",
              }}
            >
              <span className="text-[#dfddd6]/65">India</span>
              <span className="text-[#888680]/40"> could be </span>
              <span className="text-[#dfddd6]/65">taxing</span>
              <span className="text-[#888680]/40"> your</span>
            </p>
            <h1
              className="font-black uppercase leading-none mb-3"
              style={{
                fontFamily: "'Inter', Arial, sans-serif",
                fontSize: "clamp(3rem, 2.4rem + 3.5vw, 5.5rem)",
                letterSpacing: "-0.03em",
              }}
            >
              <span className="text-[#d9fb06]">Foreign Income</span>
            </h1>
            <p
              className="text-[#dfddd6] font-black uppercase"
              style={{
                fontFamily: "'Inter', Arial, sans-serif",
                fontSize: "clamp(1.8rem, 1.4rem + 2vw, 3rem)",
                letterSpacing: "-0.02em",
              }}
            >
              Right Now.
            </p>
          </div>

          {/* Secondary line — de-emphasized, italic contrast */}
          <p
            className="text-[#888680]/60 font-medium mb-10 max-w-lg"
            style={{
              fontFamily: "'Inter', Arial, sans-serif",
              fontSize: "clamp(1rem, 0.85rem + 0.8vw, 1.25rem)",
              fontStyle: "italic",
            }}
          >
            And your tax consultant{" "}
            <span className="text-[#dfddd6] not-italic font-bold">probably has no idea.</span>
          </p>

          {/* Subheadline */}
          <p
            className="text-[#888680] text-base leading-relaxed mb-10 max-w-xl border-l-2 border-[#3f4816]/40 pl-4"
            style={{ fontFamily: "'Inter', Arial, sans-serif" }}
          >
            Get your complete{" "}
            <span className="text-[#dfddd6] font-semibold">NRI Tax Position Report</span>{" "}
            in <span className="text-[#d9fb06] font-bold">48 hours</span> — find out exactly what
            you owe, what you've overpaid, and whether an IT notice is already building against your name.
          </p>

          <div className="flex flex-wrap gap-3 mb-10">
            {nriHeroData.socialProof.map((item, i) => {
              const Icon = socialIcons[i];
              const isHighlight = item.label === "Avg Tax Saved";
              return (
                <div
                  key={i}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-full border ${
                    isHighlight
                      ? "bg-[#d9fb06]/10 border-[#d9fb06]/40"
                      : "bg-[#302f2c]/70 border-[#3f4816]/30"
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isHighlight ? "bg-[#d9fb06]/20" : "bg-[#3f4816]/30"}`}>
                    <Icon size={12} className="text-[#d9fb06]" />
                  </div>
                  <span
                    className="text-[#d9fb06] font-black"
                    style={{
                      fontFamily: "'Inter', Arial, sans-serif",
                      letterSpacing: "-0.01em",
                      fontSize: isHighlight ? "1.05rem" : "0.875rem",
                    }}
                  >
                    {item.value}
                  </span>
                  <span
                    className={`text-xs ${isHighlight ? "text-[#dfddd6] font-semibold" : "text-[#888680]"}`}
                    style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                  >
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Primary CTA — WhatsApp */}
          <a
            href="#nri-bonuses"
            onClick={(e) => { e.preventDefault(); document.querySelector("#nri-bonuses")?.scrollIntoView({ behavior: "smooth" }); }}
            data-testid="nri-hero-whatsapp-cta"
            className="group inline-flex items-center gap-3 bg-[#d9fb06] text-[#1a1c1b] font-bold text-sm px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.02] hover:opacity-90 active:scale-[0.98] shadow-lg shadow-[#d9fb06]/10"
            style={{ fontFamily: "'Inter', Arial, sans-serif", letterSpacing: "0.01em" }}
          >
            <span>Get My NRI Tax Report + Filing</span>
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 flex-shrink-0" />
          </a>

          {/* Trust line */}
          <p
            className="mt-4 text-[#888680]/50 text-xs flex items-center gap-2"
            style={{ fontFamily: "'Inter', Arial, sans-serif" }}
          >
            <Clock size={12} className="text-[#3f4816]" />
            {nriHeroData.trustLine}
          </p>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-32"
        style={{ background: "linear-gradient(to top, #1a1c1b 0%, transparent 100%)" }}
      />
    </section>
  );
};
